/**
 * Catálogo de perguntas estratégicas do "Pergunte ao Edital".
 *
 * Cada pergunta carrega um `id` estável usado como `strategic_question_id`
 * no POST de mensagens. O conteúdo da instrução adicional (oculta do utilizador)
 * vive no serviço de IA, indexado por esse `id`.
 *
 * Regras:
 *  - NUNCA alterar o `id` de uma pergunta após publicada — quebra a associação
 *    com a instrução no serviço de IA.
 *  - O `text` pode ser editado livremente (mudanças de copy não afetam o id).
 *  - Convenção de id: `topico[.subtopico].slug-curto` em kebab-case.
 *  - Adicionar perguntas novas com ids únicos; o serviço de IA pode tolerar
 *    ids desconhecidos (no-op).
 */

export interface StrategicQuestion {
  id: string;
  text: string;
}

export interface StrategicQuestionSubgroup {
  id: string;
  title: string;
  questions: StrategicQuestion[];
}

export interface StrategicQuestionGroup {
  id: string;
  title: string;
  preambleQuestions?: StrategicQuestion[];
  subgroups?: StrategicQuestionSubgroup[];
  questions?: StrategicQuestion[];
}

export const STRATEGIC_QUESTION_GROUPS: StrategicQuestionGroup[] = [
  {
    id: 'topico-proposta',
    title: 'Proposta',
    questions: [
      {
        id: 'proposta.preenchimento-envio',
        text: 'Como deve ser feito o preenchimento e envio da proposta?',
      },
      { id: 'proposta.indicacao-marca', text: 'Há indicação de marca?' },
      {
        id: 'proposta.vedacao-identificacao',
        text: 'Há vedação à identificação da licitante?',
      },
      {
        id: 'proposta.desclassificacao-inexequivel',
        text: 'Há risco de desclassificação por preço inexequível? Em quais hipóteses?',
      },
      {
        id: 'proposta.parametros-exequibilidade',
        text: 'O edital estabelece parâmetros objetivos para análise de exequibilidade?',
      },
      {
        id: 'proposta.desempate-me-epp',
        text: 'Como ocorre o desempate de propostas, inclusive para ME/EPP?',
      },
      {
        id: 'proposta.arredondamento-formacao-preco',
        text: 'Existem regras específicas para arredondamento, casas decimais ou formação do preço?',
      },
      {
        id: 'proposta.documentos-acompanhamento',
        text: 'A proposta deverá conter ou estar acompanhada de algum documento?',
      },
      {
        id: 'proposta.criterio-julgamento',
        text: 'Qual é o critério de julgamento?',
      },
    ],
  },
  {
    id: 'topico-objeto',
    title: 'Objeto',
    questions: [
      { id: 'objeto.objeto-licitacao', text: 'Qual é o objeto da licitação?' },
      {
        id: 'objeto.especificacoes-tecnicas',
        text: 'Quais são as especificações técnicas e condições de fornecimento do objeto?',
      },
      {
        id: 'objeto.especificacao-tr',
        text: 'Há alguma especificação técnica no Termo de Referência?',
      },
      {
        id: 'objeto.produtos-equivalentes',
        text: 'O objeto permite o fornecimento de produtos ou serviços equivalentes?',
      },
      {
        id: 'objeto.fornecedores-segmento',
        text: 'Os itens podem ser fornecidos por empresas que não atuam exclusivamente neste segmento?',
      },
      { id: 'objeto.cnae', text: 'Há alguma menção à CNAE específico?' },
      {
        id: 'objeto.divisao-itens-lotes',
        text: 'O objeto está dividido em itens ou lotes? A divisão amplia ou restringe a competitividade?',
      },
      {
        id: 'objeto.aglutinacao-indevida',
        text: 'Há indícios de aglutinação indevida?',
      },
      {
        id: 'objeto.valor-orcamento',
        text: 'Qual é o valor/orçamento estimado?',
      },
      {
        id: 'objeto.itens-homogeneos',
        text: 'Os itens são homogêneos ou misturam naturezas diferentes?',
      },
      {
        id: 'objeto.servicos-distintos',
        text: 'Há serviços distintos no mesmo item?',
      },
      {
        id: 'objeto.justificativa-parcelamento',
        text: 'Existe justificativa técnica para a forma de parcelamento (ou ausência dele)?',
      },
      {
        id: 'objeto.especificacoes-suficientes',
        text: 'As especificações são objetivas, completas e suficientes? Há omissões relevantes?',
      },
      {
        id: 'objeto.detalhamento-restritivo',
        text: 'Há excesso de detalhamento restritivo?',
      },
      {
        id: 'objeto.indicacao-marca-modelo',
        text: 'Há indicação de marca, modelo ou tecnologia específica?',
      },
      {
        id: 'objeto.justificativa-tecnica',
        text: 'Existe justificativa técnica?',
      },
      {
        id: 'objeto.solucoes-equivalentes',
        text: 'O termo de referência/projeto básico permite soluções equivalentes?',
      },
      {
        id: 'objeto.clausula-ou-equivalente',
        text: 'Há cláusula de “ou equivalente”?',
      },
      {
        id: 'objeto.quantitativos',
        text: 'Quais são os quantitativos? Eles são reais, históricos ou estimados sem base concreta?',
      },
      { id: 'objeto.consumo-minimo', text: 'Existe consumo mínimo?' },
      {
        id: 'objeto.acrescimos-supressoes',
        text: 'Há previsão de acréscimos ou supressões?',
      },
      {
        id: 'objeto.prazos-exequibilidade',
        text: 'Os prazos são exequíveis na prática? A entrega é única ou continuada?',
      },
      {
        id: 'objeto.prazo-entrega-execucao',
        text: 'Qual é o prazo de entrega e/ou execução/conclusão?',
      },
      {
        id: 'objeto.local-entrega',
        text: 'Qual é o local de entrega? Há múltiplas localidades?',
      },
      {
        id: 'objeto.prazo-entrega-mercado',
        text: 'Prazo de entrega compatível com o mercado?',
      },
    ],
  },
  {
    id: 'topico-habilitacao',
    title: 'Habilitação',
    preambleQuestions: [
      {
        id: 'habilitacao.inicio-fase',
        text: 'Quando inicia a fase de habilitação?',
      },
      {
        id: 'habilitacao.prazo-envio-documentos',
        text: 'Qual é o prazo para envio dos documentos de habilitação?',
      },
    ],
    subgroups: [
      {
        id: 'habilitacao-juridica',
        title: 'Jurídica',
        questions: [
          {
            id: 'habilitacao.juridica.documentos',
            text: 'Quais são os documentos exigidos para fins de comprovação da habilitação jurídica?',
          },
          {
            id: 'habilitacao.juridica.declaracao',
            text: 'Há alguma declaração a ser apresentada como condição de habilitação?',
          },
        ],
      },
      {
        id: 'habilitacao-regularidade',
        title: 'Regularidade fiscal, social e trabalhista',
        questions: [
          {
            id: 'habilitacao.regularidade.documentos',
            text: 'Quais são os documentos exigidos para fins de comprovação da regularidade fiscal, social e trabalhista?',
          },
        ],
      },
      {
        id: 'habilitacao-qualificacao-tecnica',
        title: 'Qualificação técnica operacional e profissional',
        questions: [
          {
            id: 'habilitacao.tecnica.atestado',
            text: 'O edital exige a apresentação de atestado de capacidade técnica?',
          },
          {
            id: 'habilitacao.tecnica.quantitativo-minimo',
            text: 'O edital exige quantitativo mínimo nos atestados?',
          },
          {
            id: 'habilitacao.tecnica.exigencia-incomum',
            text: 'Há alguma exigência de habilitação incomum ou restritiva em relação ao objeto?',
          },
          {
            id: 'habilitacao.tecnica.soma-atestados',
            text: 'É permitida a soma de atestados para comprovação da capacidade técnica?',
          },
          {
            id: 'habilitacao.tecnica.registro-conselho',
            text: 'Existe exigência de registro em conselho profissional? Se sim, em qual?',
          },
          {
            id: 'habilitacao.tecnica.exigencia-tr',
            text: 'Há alguma exigência referente à qualificação técnica no Termo de Referência?',
          },
          {
            id: 'habilitacao.tecnica.declaracao',
            text: 'Há alguma declaração a ser apresentada?',
          },
          {
            id: 'habilitacao.tecnica.profissional-habilitado',
            text: 'Há exigência de declaração atestando que possui profissional legalmente habilitado, com formação superior compatível e devidamente registrado no conselho profissional?',
          },
        ],
      },
      {
        id: 'habilitacao-qualificacao-economica',
        title: 'Qualificação econômico-financeira',
        questions: [
          {
            id: 'habilitacao.economica.balanco-patrimonial',
            text: 'Há exigência de apresentação de balanço patrimonial e demonstrações contábeis dos dois últimos exercícios sociais, já exigíveis e apresentados na forma da Lei, que comprovem a boa situação financeira da empresa, vedada a sua substituição por balancetes ou balanços provisórios?',
          },
          {
            id: 'habilitacao.economica.certidao-falencia',
            text: 'Há exigência de apresentação de certidão negativa de feitos sobre falência expedida pelo distribuidor da sede do licitante?',
          },
          {
            id: 'habilitacao.economica.capital-minimo',
            text: 'Há exigência de comprovação do capital mínimo?',
          },
          {
            id: 'habilitacao.economica.capital-social',
            text: 'Há exigência de comprovação do capital social?',
          },
          {
            id: 'habilitacao.economica.outras-exigencias',
            text: 'Há alguma outra exigência referente ao tópico “Habilitação / Qualificação econômico-financeira”?',
          },
        ],
      },
    ],
  },
  {
    id: 'topico-pagamentos',
    title: 'Pagamentos',
    questions: [
      {
        id: 'pagamentos.prazo',
        text: 'Há previsão clara de prazo de pagamento?',
      },
      {
        id: 'pagamentos.formas-condicoes',
        text: 'Quais são as formas e condições de pagamento?',
      },
      {
        id: 'pagamentos.correcao-atraso',
        text: 'Há correção por atraso (juros e multa)?',
      },
      {
        id: 'pagamentos.condicionante',
        text: 'Há alguma condicionante para realização do pagamento?',
      },
      {
        id: 'pagamentos.marcos-objetivos',
        text: 'Pagamentos atrelados a marcos objetivos?',
      },
      {
        id: 'pagamentos.medicao',
        text: 'Há medição? Se sim, como será feita?',
      },
    ],
  },
  {
    id: 'topico-riscos-penalidades',
    title: 'Riscos e Penalidades',
    questions: [
      {
        id: 'riscos.diagnostico',
        text: 'Detecte os Riscos jurídicos e pontos de atenção do Edital, Termo de Referência e do Contrato.',
      },
      { id: 'riscos.penalidades', text: 'Elenque todas as penalidades.' },
      {
        id: 'riscos.clausulas-desproporcionais',
        text: 'Há cláusulas que podem gerar penalidades desproporcionais?',
      },
      {
        id: 'riscos.penalidades-cumulativas',
        text: 'Há penalidades cumulativas?',
      },
      {
        id: 'riscos.retencao-pagamentos',
        text: 'Há previsão para retenção dos pagamentos devidos?',
      },
      {
        id: 'riscos.contradicoes-internas',
        text: 'O Edital, o Termo de Referência e o Contrato apresentam contradições internas que merecem impugnação?',
      },
      {
        id: 'riscos.exigencias-questionaveis',
        text: 'Existem exigências que podem ser consideradas ilegais ou questionáveis à luz da Lei 14.133?',
      },
      {
        id: 'riscos.prazo-logistica-execucao',
        text: 'Há riscos relacionados a prazo, logística ou execução contratual?',
      },
      {
        id: 'riscos.fundamentos-impugnacao',
        text: 'Existem fundamentos para impugnação do edital?',
      },
      {
        id: 'riscos.matriz-risco',
        text: 'Há matriz de risco? Se sim, ela está bem definida?',
      },
      {
        id: 'riscos.riscos-excessivos',
        text: 'O edital transfere riscos excessivos ao contratado?',
      },
      {
        id: 'riscos.garantia-produto',
        text: 'Há exigência de garantia do produto?',
      },
      {
        id: 'riscos.garantia-proposta',
        text: 'Há exigência de garantia da proposta?',
      },
      {
        id: 'riscos.garantia-contratual',
        text: 'Há exigência de garantia contratual?',
      },
      {
        id: 'riscos.reajuste-repactuacao',
        text: 'Há previsão clara sobre reajuste, repactuação e reequilíbrio econômico-financeiro?',
      },
    ],
  },
  {
    id: 'topico-contrato',
    title: 'Contrato',
    questions: [
      {
        id: 'contrato.art-92',
        text: 'Faça um diagnóstico sobre as exigências do Art. 92 da Lei 14.133/21.',
      },
      {
        id: 'contrato.divergencia-tr',
        text: 'Há alguma divergência entre o contrato e o termo de referência?',
      },
      {
        id: 'contrato.obrigacoes-contratado',
        text: 'Quais são as obrigações do contratado?',
      },
    ],
  },
];
